import { useState } from 'react';
import { createEquipmentPictures } from '@services/queries/pictures/createEquipmentPicture/createEquipmentPicture';
import { isImageUrl } from '@utils';

import * as yup from 'yup';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import { Icon as BlueIcon } from '@blueprintjs/core';
import { Portal } from '@mantine/core';
import {
  Overlay,
  Container,
  RowDiv,
  Text,
  Button,
  ColumnDiv,
  Input,
  AddButton,
  FileName,
  IconCloseAwesome,
  SelectFile,
  Divider,
  PictureInputRow,
  Title,
} from './PicturesAdd.styles';

interface IPicturesAdd {
  setShowAdd: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IFormData {
  name: string;
  uri: string;
  file?: FileList;
}

const schema = yup
  .object({
    name: yup.string().required(),
    uri: yup.string(),
    file: yup.mixed(),
  })
  .required();

const PicturesAdd = ({ setShowAdd }: IPicturesAdd) => {
  const queryClient = useQueryClient();
  const params = useParams<{ equipmentId: string }>();

  const [pictureName, setPictureName] = useState<string>('');
  const [pictureUri, setPictureUri] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  const mutationCreatePicture = useMutation(createEquipmentPictures, {
    onSuccess: () => {
      setShowAdd(false);
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'pictures',
      ]);
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid, errors },
    setValue,
  } = useForm<IFormData>({
    defaultValues: {
      name: '',
      uri: '',
    },
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const onSubmit = (data: IFormData) => {
    const result = data.uri
      ? {
          id: params.equipmentId,
          type: 'uri' as any,
          uri: data.uri,
          name: data.name,
        }
      : {
          id: params.equipmentId,
          name: data.name,
          type: 'file' as any,
          file: data.file?.[0],
        };
    if (!mutationCreatePicture.isLoading) {
      mutationCreatePicture.mutate(result);
    }
  };

  const Picture = () => {
    if (pictureUri !== '')
      return (
        <Input
          type="text"
          placeholder="Upload an image url or select a file"
          {...register('uri')}
          isLink={isImageUrl(pictureUri)}
          onChange={e => {
            register('uri').onChange(e);
            setPictureUri(e.target.value);
          }}
          isError={!!errors.uri?.message}
        />
      );
    if (fileName !== '')
      return (
        <FileName>
          {fileName}
          <IconCloseAwesome
            icon={faTimes}
            onClick={() => {
              setFileName('');
              setValue('file', undefined);
            }}
          />
        </FileName>
      );
    return (
      <PictureInputRow>
        <Input
          type="text"
          placeholder="Image URL or Select a File"
          {...register('uri')}
          onChange={e => {
            register('uri').onChange(e);
            setPictureUri(e.target.value);
          }}
        />

        <SelectFile htmlFor="upload">Select File</SelectFile>
        <input
          type="file"
          id="upload"
          hidden
          accept="image/*"
          {...register('file')}
          onChange={e => {
            register('file').onChange(e);
            setFileName(e.target.files?.[0]?.name || '');
          }}
        />
      </PictureInputRow>
    );
  };

  return (
    <Portal>
      <Overlay>
        <Container>
          <RowDiv
            style={{
              justifyContent: 'space-between',
              alignSelf: 'flex-start',
            }}
          >
            <Title>Add Photo</Title>

            <Button onClick={() => setShowAdd(false)}>
              <BlueIcon icon="cross" color="#8F95B2" />
            </Button>
          </RowDiv>

          <Divider height="28px" />

          <form style={{ width: '100%' }} onSubmit={handleSubmit(onSubmit)}>
            <ColumnDiv>
              <Text className="labels">Title</Text>
              <Input
                type="text"
                placeholder="Photo name"
                {...register('name')}
                onChange={e => {
                  register('name').onChange(e);
                  setPictureName(e.target.value);
                }}
                autoFocus
              />
              <div>{errors.name?.message}</div>

              <Text className="labels">Picture</Text>
              {Picture()}

              <AddButton
                type="submit"
                disabled={
                  !isValid ||
                  !pictureName ||
                  (!pictureUri && !fileName) ||
                  mutationCreatePicture.isLoading
                }
              >
                Add Photo
              </AddButton>
            </ColumnDiv>
          </form>
        </Container>
      </Overlay>
    </Portal>
  );
};

export { PicturesAdd };
